import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import img8779 from '../assets/engagementGallery/IMG_8779.jpg';
import img8785 from '../assets/engagementGallery/IMG_8785.jpg';
import img8787 from '../assets/engagementGallery/IMG_8787.jpg';
import img8789 from '../assets/engagementGallery/IMG_8789.jpg';
import img8804 from '../assets/engagementGallery/IMG_8804.jpg';
import img8805 from '../assets/engagementGallery/IMG_8805.jpg';
import img8808 from '../assets/engagementGallery/IMG_8808.jpg';
import img8825 from '../assets/engagementGallery/IMG_8825.jpg';
import img8828 from '../assets/engagementGallery/IMG_8828.jpg';
import img8840 from '../assets/engagementGallery/IMG_8840.jpg';
import img8854 from '../assets/engagementGallery/IMG_8854.jpg';
import img8857 from '../assets/engagementGallery/IMG_8857.jpg';

// Landscape shots are interleaved among the portraits so each masonry
// column gets a mix of shapes.
const photos = [
  img8785, img8779, img8787, img8804, img8789, img8805,
  img8808, img8825, img8857, img8828, img8840, img8854
];

const PageContainer = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 1rem 2rem 4rem;

  @media (max-width: 768px) {
    padding: 0.5rem 1rem 3rem;
  }
`;

const GalleryHeader = styled.div`
  text-align: center;
  margin-bottom: 2.5rem;
`;

const Title = styled.h1`
  font-size: 2.2rem;
  letter-spacing: 3px;
  margin: 0 0 0.75rem 0;
  color: #333;
`;

const Subtitle = styled.div`
  font-family: 'Montserrat', sans-serif;
  font-size: 0.8rem;
  letter-spacing: 3px;
  color: #666;
  text-transform: uppercase;
`;

const HeaderRule = styled.hr`
  border: none;
  border-top: 1px solid #ddd;
  width: 60px;
  margin: 1.25rem auto 0;
`;

const Masonry = styled.div`
  column-count: 3;
  column-gap: 1.25rem;

  @media (max-width: 900px) {
    column-count: 2;
    column-gap: 1rem;
  }

  @media (max-width: 560px) {
    column-count: 2;
    column-gap: 0.6rem;
  }
`;

const PhotoFrame = styled.button`
  display: block;
  width: 100%;
  margin: 0 0 1.25rem;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  break-inside: avoid;
  overflow: hidden;

  @media (max-width: 560px) {
    margin-bottom: 0.6rem;
  }
`;

const GalleryImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
  transition: transform 0.6s ease, opacity 0.6s ease;

  ${PhotoFrame}:hover & {
    transform: scale(1.03);
  }
`;

const Lightbox = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.98);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const LightboxImage = styled.img`
  max-width: 92vw;
  max-height: 84vh;
  object-fit: contain;
  user-select: none;
`;

const LightboxButton = styled.button`
  position: fixed;
  background: none;
  border: none;
  color: #fff;
  font-family: 'Cormorant Garamond', serif;
  cursor: pointer;
  z-index: 1001;
  padding: 0.5rem 1rem;
  min-width: 44px;
  min-height: 44px;
  opacity: 0.8;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }
`;

const CloseButton = styled(LightboxButton)`
  top: 12px;
  right: 12px;
  font-size: 2.2rem;
  line-height: 1;
`;

const PrevButton = styled(LightboxButton)`
  left: 4px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 3rem;
  line-height: 1;
`;

const NextButton = styled(LightboxButton)`
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 3rem;
  line-height: 1;
`;

const Counter = styled.div`
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  color: #fff;
  font-family: 'Montserrat', sans-serif;
  font-size: 0.75rem;
  letter-spacing: 3px;
  opacity: 0.8;
  z-index: 1001;
`;

function Pictures() {
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const touchStartX = useRef(null);

  const showPrev = useCallback(() => {
    setLightboxIndex(i => (i === null ? null : (i + photos.length - 1) % photos.length));
  }, []);

  const showNext = useCallback(() => {
    setLightboxIndex(i => (i === null ? null : (i + 1) % photos.length));
  }, []);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setLightboxIndex(null);
      else if (e.key === 'ArrowLeft') showPrev();
      else if (e.key === 'ArrowRight') showNext();
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [lightboxIndex, showPrev, showNext]);

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (delta > 50) showPrev();
    else if (delta < -50) showNext();
  };

  return (
    <PageContainer>
      <GalleryHeader>
        <Title>Engagement</Title>
        <Subtitle>A Few of Our Favorite Moments</Subtitle>
        <HeaderRule />
      </GalleryHeader>

      <Masonry>
        {photos.map((src, index) => (
          <PhotoFrame
            key={src}
            onClick={() => setLightboxIndex(index)}
            aria-label={`View photo ${index + 1} of ${photos.length}`}
          >
            <GalleryImage src={src} alt={`Engagement photo ${index + 1}`} loading="lazy" />
          </PhotoFrame>
        ))}
      </Masonry>

      {lightboxIndex !== null && (
        <Lightbox
          onClick={(e) => { if (e.target === e.currentTarget) setLightboxIndex(null); }}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <LightboxImage
            src={photos[lightboxIndex]}
            alt={`Engagement photo ${lightboxIndex + 1}`}
          />
          <CloseButton onClick={() => setLightboxIndex(null)} aria-label="Close">×</CloseButton>
          <PrevButton onClick={showPrev} aria-label="Previous photo">‹</PrevButton>
          <NextButton onClick={showNext} aria-label="Next photo">›</NextButton>
          <Counter>{lightboxIndex + 1} / {photos.length}</Counter>
        </Lightbox>
      )}
    </PageContainer>
  );
}

export default Pictures;
